import React from "react";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import Icon from "../../../../components/base/Icon";
import Indicator from "../../../../components/base/Indicator";
import AddonCatalog, { AddonState } from "../models/AddonCatalog";
import "./AddonsCard.css";


// Everything that can go on the account, and where each one stands for this customer.
//
// Nothing is hidden. Something the plan already includes stays on the shelf, marked as theirs,
// because "do I already have this?" is one of the questions that brings somebody here — and an
// empty space is not an answer to it.
export default function AddonLibraryDrawer(props: {
    items?: AddonCatalog[],
    loading?: boolean,
    // Held by the page so it can ask this to close after a purchase. Unmounting it instead would
    // leave the backdrop behind: only the drawer's own hide takes that down.
    drawerRef?: React.RefObject<DrawerRef>,
    onClose: () => void,
    onAdd: (item: AddonCatalog) => void
}) {

    const items = props.items ?? []

    const row = (item: AddonCatalog, key: number) => {

        const owned = item.state === AddonState.Included || item.state === AddonState.Active
        const off = item.state === AddonState.NotOffered

        return (
            <div key={key}
                className={"addon-lib-item" + (owned ? " is-owned" : "") + (off ? " is-off" : "")}>

                <div className="addon-lib-icon">
                    {
                        item.image ?
                            <img src={item.image} alt={item.name} />
                            :
                            <Icon icon={item.icon} type={item.iconType} size={22} />
                    }
                </div>

                <div className="addon-lib-body">

                    <div className="addon-lib-head">
                        <span className="addon-lib-name">{item.name}</span>
                        <span className="addon-lib-price">{item.priceText}</span>
                    </div>

                    <div className="addon-lib-desc">{item.description}</div>

                    {
                        (item.features?.length ?? 0) > 0 &&
                        <div className="addon-lib-features">
                            {
                                item.features!.map((f, k) =>
                                    <span className="addon-lib-feature" key={k}>
                                        <Icon icon="check" type="solid" size={9} />
                                        {f}
                                    </span>
                                )
                            }
                        </div>
                    }

                    <div className="addon-lib-action">
                        {item.note && <span className="addon-lib-note">{item.note}</span>}

                        {
                            item.canPurchase ?
                                <button type="button" className="btn btn-sm btn-primary"
                                    onClick={e => { e.preventDefault(); props.onAdd(item); }}>
                                    <Icon icon="plus" type="solid" size={11} className="me-2" />
                                    {
                                        /* Extra users can be bought again while some are running,
                                           so the button says which of the two this is. */
                                        item.byQuantity
                                            ? ((item.quantity ?? 0) > 0 ? "Add more users" : "Add users") + " · " + item.priceText
                                            : "Add for " + item.priceText
                                    }
                                </button>
                                :
                                <span className={"badge fw-bold px-3 py-2 "
                                    + (owned ? "badge-light-success" : off ? "badge-light-danger" : "badge-light-warning")}>
                                    {
                                        owned &&
                                        <Icon icon="circle-check" type="solid" size={10} className="me-1" />
                                    }
                                    {item.stateText}
                                </span>
                        }
                    </div>

                </div>
            </div>
        )
    }

    // What can be bought first, then what is already theirs, then what this plan does not sell.
    // Somebody who opened a library came to find something new.
    const order = (i: AddonCatalog) =>
        i.canPurchase ? 0
            : i.state === AddonState.ComingSoon ? 1
                : i.state === AddonState.Included || i.state === AddonState.Active ? 2
                    : 3

    const sorted = [...items].sort((a, b) => order(a) - order(b))

    return (
        <Drawer ref={props.drawerRef} id="drawer-addon-library"
            title="Addon Library"
            titleIcon="grid-2-plus"
            subtitle="Everything you can add to your account"
            size="medium"
            onClose={props.onClose}>

            {
                props.loading ?
                    <div className="py-20">
                        <Indicator />
                    </div>
                    :
                    <div className="addon-library">
                        {sorted.map((item, key) => row(item, key))}
                    </div>
            }

        </Drawer>
    )
}
